/**
 * CommandPalette — Cmd/Ctrl-K quick nav + global /search.
 *
 * Two layers of suggestions:
 *   1. **Routes** — synchronous, role-filtered nav targets. Always shown when
 *      there's no query (palette acts as a quick launcher).
 *   2. **Search** — debounced call to `/search` for employees, documents,
 *      tasks. Layered under matching routes so navigation never feels slower
 *      than the local list.
 *
 * Implements WAI-ARIA "combobox with listbox":
 *   - input[role=combobox] with aria-controls/aria-expanded/aria-autocomplete
 *   - results listbox; each row is role="option" with aria-selected
 *
 * Keyboard:
 *   ArrowDown / ArrowUp  — move highlight (wraps)
 *   Enter                — activate
 *   Escape               — close (Headless UI handles)
 *   Home / End           — jump to first / last
 */
import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Search, User, FileText, CheckSquare, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { navRoutes } from "@/lib/routes";
import { useAuth } from "@/hooks/useAuth";
import { useDebounce } from "@/hooks/useDebounce";
import { unifiedSearch, type SearchHit } from "@/api/search";

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

const LISTBOX_ID = "command-palette-listbox";

interface PaletteItem {
  id: string;
  label: string;
  hint?: string;
  url: string;
  icon: LucideIcon;
  group: "Pages" | "People" | "Documents" | "Tasks";
}

const KIND_ICON: Record<SearchHit["kind"], LucideIcon> = {
  employee: User,
  document: FileText,
  task: CheckSquare,
};

const KIND_GROUP: Record<SearchHit["kind"], PaletteItem["group"]> = {
  employee: "People",
  document: "Documents",
  task: "Tasks",
};

export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const [searchHits, setSearchHits] = useState<SearchHit[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const resultsRef = useRef<HTMLDivElement>(null);

  const debouncedQuery = useDebounce(query.trim(), 200);

  // Local route matches — instant, no network.
  const matchedRoutes = useMemo<PaletteItem[]>(() => {
    const allowed = navRoutes.filter(
      (r) => user && r.roles.includes(user.role),
    );
    const labeled = allowed.map((r) => ({
      ...r,
      displayLabel: t(r.labelKey, { defaultValue: r.label }),
    }));
    const filtered = query
      ? labeled.filter((r) =>
          r.displayLabel.toLowerCase().includes(query.toLowerCase()),
        )
      : labeled;
    return filtered.map((r) => ({
      id: `route-${r.path}`,
      label: r.displayLabel,
      url: r.path,
      icon: r.icon,
      group: "Pages" as const,
    }));
  }, [query, user, t]);

  // Server search — debounced. Cancel via "current request id" pattern so a
  // late response doesn't overwrite a newer one.
  useEffect(() => {
    if (!debouncedQuery) {
      setSearchHits([]);
      setSearchLoading(false);
      return;
    }
    let cancelled = false;
    setSearchLoading(true);
    unifiedSearch(debouncedQuery, { limit: 5 })
      .then((res) => {
        if (!cancelled) setSearchHits(res.results);
      })
      .catch((err) => {
        // Silent to the user — the palette stays usable on local route
        // matches even if /search is down — but we log so broken search
        // shows up in dev tools / telemetry instead of disappearing.
        console.error("CommandPalette: unified search failed", err);
        if (!cancelled) setSearchHits([]);
      })
      .finally(() => {
        if (!cancelled) setSearchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  const items = useMemo<PaletteItem[]>(() => {
    const remoteItems: PaletteItem[] = searchHits.map((h) => ({
      id: `${h.kind}-${h.id}`,
      label: h.title,
      hint: h.subtitle,
      url: h.url,
      icon: KIND_ICON[h.kind],
      group: KIND_GROUP[h.kind],
    }));
    return [...matchedRoutes, ...remoteItems];
  }, [matchedRoutes, searchHits]);

  // Reset highlight when the result set shifts beneath the user.
  useEffect(() => {
    setHighlight(0);
  }, [items.length]);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setSearchHits([]);
    }
  }, [isOpen]);

  // Keep the highlighted row visible on arrow-key nav.
  useEffect(() => {
    const el = resultsRef.current?.querySelector<HTMLElement>(
      `[data-index="${highlight}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [highlight]);

  const handleSelect = (url: string) => {
    navigate(url);
    setQuery("");
    onClose();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + items.length) % items.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = items[highlight];
      if (item) handleSelect(item.url);
    } else if (e.key === "Home") {
      e.preventDefault();
      setHighlight(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setHighlight(items.length - 1);
    }
  };

  const activeId =
    items.length > 0 ? `command-option-${items[highlight]?.id}` : undefined;

  // Group items for sectioned rendering while keeping a flat array for keyboard
  // index math.
  const groups = useMemo(() => {
    const order: PaletteItem["group"][] = [
      "Pages",
      "People",
      "Documents",
      "Tasks",
    ];
    return order
      .map((g) => ({
        group: g,
        items: items
          .map((it, idx) => ({ ...it, index: idx }))
          .filter((it) => it.group === g),
      }))
      .filter((s) => s.items.length > 0);
  }, [items]);

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog
        onClose={onClose}
        className="relative z-50"
        aria-label="Command palette"
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        </TransitionChild>

        <div className="fixed inset-0 flex items-start justify-center pt-[20vh] px-4">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            {/* Bento-language modal: 20px radius, brand-blue glow ring,
                soft shadow that lifts the palette above the dimmed bg. */}
            <DialogPanel className="w-full max-w-xl rounded-bento bg-surface border border-accent/20 overflow-hidden shadow-[0_24px_80px_rgba(15,16,41,0.18),0_0_0_1px_rgba(27,24,255,0.08)]">
              <div className="flex items-center gap-3 px-5 border-b border-border">
                <Search
                  className="h-5 w-5 text-accent shrink-0"
                  aria-hidden="true"
                />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t("search.placeholder")}
                  aria-label={t("search.placeholder")}
                  role="combobox"
                  aria-expanded
                  aria-controls={LISTBOX_ID}
                  aria-autocomplete="list"
                  aria-activedescendant={activeId}
                  className="w-full py-3 text-sm bg-transparent text-text-primary placeholder:text-text-muted focus:outline-none"
                  autoFocus
                />
                {searchLoading && (
                  <span
                    className="text-xs text-text-muted shrink-0"
                    aria-live="polite"
                  >
                    Searching…
                  </span>
                )}
              </div>

              {/* Live region announces match count for screen readers. */}
              <div className="sr-only" role="status" aria-live="polite">
                {items.length} result{items.length === 1 ? "" : "s"}
              </div>

              <div
                ref={resultsRef}
                id={LISTBOX_ID}
                role="listbox"
                aria-label="Command results"
                className="max-h-80 overflow-y-auto py-2"
              >
                {items.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-text-muted text-center">
                    {query ? t("common.noResults") : t("search.placeholder")}
                  </p>
                ) : (
                  groups.map((section) => (
                    <div key={section.group} className="mb-1 last:mb-0">
                      <p className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider text-text-muted">
                        {t(
                          `search.groups.${(
                            {
                              Pages: "pages",
                              People: "people",
                              Documents: "documents",
                              Tasks: "tasks",
                            } as const
                          )[section.group]}`,
                        )}
                      </p>
                      {section.items.map((item) => {
                        const isHighlighted = item.index === highlight;
                        return (
                          <button
                            key={item.id}
                            type="button"
                            id={`command-option-${item.id}`}
                            role="option"
                            aria-selected={isHighlighted}
                            data-index={item.index}
                            onClick={() => handleSelect(item.url)}
                            onMouseEnter={() => setHighlight(item.index)}
                            className={cn(
                              "w-full flex items-start gap-3 px-4 py-2.5 text-sm text-text-primary transition-colors text-left",
                              isHighlighted
                                ? "bg-accent/10"
                                : "hover:bg-accent/5",
                            )}
                          >
                            <item.icon
                              className="h-4 w-4 text-text-muted mt-0.5 shrink-0"
                              aria-hidden="true"
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate">{item.label}</span>
                              {item.hint && (
                                <span className="block text-xs text-text-muted truncate">
                                  {item.hint}
                                </span>
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ))
                )}
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
